import { useState, useEffect } from 'react';
import { X, CheckCircle, Clock, FileText, CreditCard, Shield, ArrowRight, AlertTriangle } from 'lucide-react';
import KYCPromptModal from './KYCPromptModal';
import KYCModal from './KYCModal';

const STEPS = [
  { id: 1, name: 'Review', icon: FileText },
  { id: 2, name: 'EOI Payment', icon: CreditCard },
  { id: 3, name: 'Confirmation', icon: CheckCircle },
];

export default function InvestmentBookingModal({ 
  isOpen, 
  onClose, 
  unit, 
  project, 
  investmentAmount,
  onSuccess 
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [investmentId, setInvestmentId] = useState(null);
  const [agreed, setAgreed] = useState({ terms: false, risks: false });
  
  // KYC state
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [showKYCPrompt, setShowKYCPrompt] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check KYC status when modal opens
  useEffect(() => {
    if (isOpen) {
      checkKYCStatus();
    }
  }, [isOpen]);

  const checkKYCStatus = async () => {
    setKycLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (!user.id) {
        setKycStatus('NOT_STARTED');
        setKycLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/investor/kyc/status/${user.id}`);
      const data = await response.json();
      
      setKycStatus(data.status || 'NOT_STARTED');
      setRejectionReason(data.rejectionReason);
      
      // If KYC not verified, show prompt
      if (data.status !== 'VERIFIED') {
        setShowKYCPrompt(true);
      }
    } catch (error) {
      setKycStatus('NOT_STARTED');
    } finally {
      setKycLoading(false);
    }
  };

  if (!isOpen) return null;

  // Show KYC Prompt Modal
  if (showKYCPrompt && kycStatus !== 'VERIFIED' && kycStatus !== 'SUBMITTED') {
    return (
      <>
        <KYCPromptModal
          isOpen={showKYCPrompt}
          onClose={() => {
            setShowKYCPrompt(false);
            onClose();
          }}
          onStartKYC={() => {
            setShowKYCPrompt(false);
            setShowKYCModal(true);
          }}
          rejectionReason={rejectionReason}
        />
        <KYCModal
          isOpen={showKYCModal}
          onClose={() => {
            setShowKYCModal(false);
            onClose();
          }}
          userId={JSON.parse(localStorage.getItem('user') || '{}').id}
          onComplete={() => {
            setShowKYCModal(false);
            checkKYCStatus();
          }}
        />
      </>
    );
  }

  // Show pending KYC message
  if (kycStatus === 'SUBMITTED') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-blue-400/10 border border-blue-400/30 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-xl font-medium text-white mb-2">KYC Verification Pending</h2>
          <p className="text-white/60 text-sm mb-6">
            Your KYC documents are being verified. You'll be able to invest once verification is complete.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const eoiAmount = 25000; // Fixed EOI amount
  const capitalAmount = investmentAmount - eoiAmount;
  const ownershipPercent = ((investmentAmount / (unit?.financial?.capitalTarget || 1)) * 100).toFixed(2);

  const formatINR = (val) => {
    if (!val) return '₹0';
    if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val/100000).toFixed(2)} L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const handleCreateInvestment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/investments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unitId: unit.unitId,
          investorId: user.id || 'guest',
          investorName: user.name || 'Guest User',
          investorEmail: user.email || 'guest@example.com',
          investorPhone: '+91 9999999999',
          commitmentAmount: investmentAmount
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create investment');
      }
      
      setInvestmentId(data.investmentId);
      setCurrentStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePayEOI = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // In production, this would open Razorpay checkout
      // For now, we'll simulate with a mock payment
      
      // Simulate Razorpay payment delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Record the payment (mock transaction ID for now)
      const mockTransactionId = `rzp_EOI_${Date.now()}`;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/investments/record-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          investmentId: investmentId,
          paymentType: 'eoi',
          amount: eoiAmount,
          transactionId: mockTransactionId,
          paymentMethod: 'razorpay'
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Payment recording failed');
      }
      
      setCurrentStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (currentStep === 3 && onSuccess) {
      onSuccess(investmentId);
    }
    onClose();
    // Reset state
    setCurrentStep(1);
    setInvestmentId(null);
    setAgreed({ terms: false, risks: false });
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={handleClose}>
      <div 
        className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex justify-between items-start">
          <div>
            <h2 className="text-xl text-white mb-1">Book Investment</h2>
            <p className="text-sm text-gray-500">{unit?.unitName} • {project?.name}</p>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-white/10 bg-white/5">
          <div className="flex items-center justify-between">
            {STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isComplete = step.id < currentStep;
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center gap-2 ${
                    isComplete ? 'text-[#4ADE80]' : isActive ? 'text-white' : 'text-gray-600'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${
                      isComplete ? 'bg-[#4ADE80]/20 border-[#4ADE80]' : 
                      isActive ? 'bg-white/10 border-white' : 'border-gray-700'
                    }`}>
                      {isComplete ? <CheckCircle className="w-4 h-4" /> : <StepIcon className="w-4 h-4" />}
                    </div>
                    <span className="text-sm hidden sm:block">{step.name}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`w-16 h-px mx-4 ${isComplete ? 'bg-[#4ADE80]' : 'bg-gray-700'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Step Content */}
        <div className="p-6">
          {/* Step 1: Review */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Investment Summary</h3>
                <div className="border border-white/10 divide-y divide-white/10">
                  <div className="flex justify-between p-4">
                    <span className="text-gray-400">Unit</span>
                    <span className="text-white">{unit?.unitName}</span>
                  </div>
                  <div className="flex justify-between p-4">
                    <span className="text-gray-400">LLP Entity</span>
                    <span className="text-white">{unit?.llp?.name || 'TBD'}</span>
                  </div>
                  <div className="flex justify-between p-4">
                    <span className="text-gray-400">Your Investment</span>
                    <span className="text-white font-mono">{formatINR(investmentAmount)}</span>
                  </div>
                  <div className="flex justify-between p-4">
                    <span className="text-gray-400">Ownership %</span>
                    <span className="text-[#4ADE80] font-mono">{ownershipPercent}%</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Payment Schedule</h3>
                <div className="border border-white/10 divide-y divide-white/10">
                  <div className="flex justify-between items-center p-4">
                    <div>
                      <p className="text-white">EOI Payment</p>
                      <p className="text-xs text-gray-500">Due now • Refundable</p>
                    </div>
                    <span className="text-white font-mono">{formatINR(eoiAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center p-4">
                    <div>
                      <p className="text-white">Capital Payment</p>
                      <p className="text-xs text-gray-500">Due after document signing</p>
                    </div>
                    <span className="text-gray-400 font-mono">{formatINR(capitalAmount)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm text-gray-500 uppercase tracking-wider mb-4">Investment Journey</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs">1</div>
                    <div>
                      <p className="text-white">Pay EOI (₹25,000)</p>
                      <p className="text-gray-500">Held in escrow, fully refundable</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center text-xs">2</div>
                    <div>
                      <p className="text-white">Sign LLP Documents</p>
                      <p className="text-gray-500">Sent via Zoho Sign within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500/20 text-orange-400 flex items-center justify-center text-xs">3</div>
                    <div>
                      <p className="text-white">Pay Remaining Capital</p>
                      <p className="text-gray-500">After document verification</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#4ADE80]/20 text-[#4ADE80] flex items-center justify-center text-xs">4</div>
                    <div>
                      <p className="text-white">Investment Complete</p>
                      <p className="text-gray-500">Funds released to LLP when unit is fully funded</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={agreed.terms}
                    onChange={(e) => setAgreed(prev => ({ ...prev, terms: e.target.checked }))}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-400">
                    I have read and agree to the <span className="text-white underline">Terms & Conditions</span> and <span className="text-white underline">LLP Agreement Preview</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={agreed.risks}
                    onChange={(e) => setAgreed(prev => ({ ...prev, risks: e.target.checked }))}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-400">
                    I understand the risks involved in fractional real estate investment and that past performance doesn't guarantee future returns
                  </span>
                </label>
              </div>

              <button
                onClick={handleCreateInvestment}
                disabled={!agreed.terms || !agreed.risks || loading}
                className="w-full py-4 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>Processing...</>
                ) : (
                  <>
                    Continue to Payment
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 2: EOI Payment */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <CreditCard className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl text-white mb-2">Pay EOI Amount</h3>
                <p className="text-gray-500">Your investment has been registered. Pay EOI to secure your spot.</p>
              </div>

              <div className="border border-white/10 p-6">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-gray-400">EOI Amount</span>
                  <span className="text-3xl font-mono text-white">{formatINR(eoiAmount)}</span>
                </div>
                
                <div className="space-y-2 text-sm text-gray-500 mb-6">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#4ADE80]" />
                    <span>100% Refundable until document signing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-400" />
                    <span>Documents sent within 24 hours of payment</span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4 mb-4">
                  <p className="text-xs text-gray-600 mb-3">PAYMENT METHOD</p>
                  <div className="flex gap-2">
                    <div className="flex-1 border border-[#4ADE80] bg-[#4ADE80]/10 p-3 text-center">
                      <span className="text-sm text-[#4ADE80]">Razorpay</span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 text-xs text-yellow-400 mb-4">
                  <strong>Note:</strong> This is a demo environment. In production, Razorpay checkout will open for real payment processing.
                </div>
              </div>

              <button
                onClick={handlePayEOI}
                disabled={loading}
                className="w-full py-4 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>Processing Payment...</>
                ) : (
                  <>
                    Pay {formatINR(eoiAmount)}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <button
                onClick={() => setCurrentStep(1)}
                className="w-full py-3 border border-white/10 text-gray-400 hover:text-white"
              >
                Go Back
              </button>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {currentStep === 3 && (
            <div className="space-y-6 text-center">
              <div className="py-6">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#4ADE80]/20 flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-[#4ADE80]" />
                </div>
                <h3 className="text-2xl text-white mb-2">EOI Payment Successful!</h3>
                <p className="text-gray-500">Your investment spot has been secured</p>
              </div>

              <div className="border border-white/10 p-6 text-left">
                <h4 className="text-sm text-gray-500 uppercase tracking-wider mb-4">What Happens Next?</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-white">LLP Documents (Within 24 hours)</p>
                      <p className="text-sm text-gray-500">You'll receive documents via Zoho Sign for e-signature</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-500/20 text-yellow-400 flex items-center justify-center">
                      <CreditCard className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-white">Capital Call (After signing)</p>
                      <p className="text-sm text-gray-500">Pay remaining {formatINR(capitalAmount)} to complete investment</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-[#4ADE80]/30 bg-[#4ADE80]/5 p-4">
                <p className="text-sm text-gray-400">
                  Investment ID: <span className="text-white font-mono">{investmentId}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Track your investment progress in the Portfolio section
                </p>
              </div>

              <button
                onClick={handleClose}
                className="w-full py-4 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold"
              >
                View My Portfolio
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
